/** Shared structured symbol filters (type, cap, price) for report-view and flow APIs. */

export const REFERENCE_SYMBOL_FILTERS_STORAGE_KEY = 'mmec_reference_symbol_filters_v1';
export const REFERENCE_SYMBOL_FILTERS_EVENT = 'mmec-reference-symbol-filters';

export const PRICE_BUCKET_PRESETS: { label: string; value: string }[] = [
  { label: 'Any', value: '' },
  { label: '$0–10', value: '0-10' },
  { label: '$10–20', value: '10-20' },
  { label: '$20–30', value: '20-30' },
  { label: '$30–50', value: '30-50' },
  { label: '$50–100', value: '50-100' },
  { label: '$100+', value: '100+' },
];

export type ReferenceSymbolFiltersState = {
  fIndex: boolean;
  fStock: boolean;
  fEtf: boolean;
  fCapSmall: boolean;
  fCapMed: boolean;
  fCapLarge: boolean;
  fCapUnknown: boolean;
  priceBucket: string;
  priceCustomMin: string;
  priceCustomMax: string;
  strictPrice: boolean;
};

export const DEFAULT_REFERENCE_SYMBOL_FILTERS: ReferenceSymbolFiltersState = {
  fIndex: true,
  fStock: true,
  fEtf: true,
  fCapSmall: true,
  fCapMed: true,
  fCapLarge: true,
  fCapUnknown: true,
  priceBucket: '',
  priceCustomMin: '',
  priceCustomMax: '',
  strictPrice: false,
};

export function loadReferenceSymbolFiltersState(): ReferenceSymbolFiltersState {
  if (typeof window === 'undefined') return { ...DEFAULT_REFERENCE_SYMBOL_FILTERS };
  try {
    const raw = window.localStorage.getItem(REFERENCE_SYMBOL_FILTERS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_REFERENCE_SYMBOL_FILTERS };
    const j = JSON.parse(raw) as Partial<ReferenceSymbolFiltersState>;
    return { ...DEFAULT_REFERENCE_SYMBOL_FILTERS, ...j };
  } catch {
    return { ...DEFAULT_REFERENCE_SYMBOL_FILTERS };
  }
}

export function saveReferenceSymbolFiltersState(s: ReferenceSymbolFiltersState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(REFERENCE_SYMBOL_FILTERS_STORAGE_KEY, JSON.stringify(s));
    window.dispatchEvent(new CustomEvent(REFERENCE_SYMBOL_FILTERS_EVENT));
  } catch {
    /* ignore quota */
  }
}

/** Query keys aligned with GET /api/ois/report-view and flow endpoints. */
export function referenceFiltersToQueryRecord(state: ReferenceSymbolFiltersState): Record<string, string> {
  const p: Record<string, string> = {};
  const assets: string[] = [];
  if (state.fIndex) assets.push('index');
  if (state.fStock) assets.push('stock');
  if (state.fEtf) assets.push('etf');
  if (assets.length >= 1 && assets.length < 3) {
    p.asset_class = assets.join(',');
  }
  const caps: string[] = [];
  if (state.fCapSmall) caps.push('small');
  if (state.fCapMed) caps.push('medium');
  if (state.fCapLarge) caps.push('large');
  if (state.fCapUnknown) caps.push('unknown', 'n_a');
  const capUnique = Array.from(new Set(caps));
  if (capUnique.length >= 1 && capUnique.length < 5) {
    p.cap_tier = capUnique.join(',');
  }
  if (state.priceBucket) {
    p.price_bucket = state.priceBucket;
  } else {
    if (state.priceCustomMin.trim() !== '') p.price_min = state.priceCustomMin.trim();
    if (state.priceCustomMax.trim() !== '') p.price_max = state.priceCustomMax.trim();
  }
  if (!state.fCapUnknown && (state.fCapSmall || state.fCapMed || state.fCapLarge)) {
    p.include_unknown_cap = '0';
  }
  if (state.strictPrice) p.strict_price = '1';
  return p;
}

export function referenceFiltersReportViewSuffix(state: ReferenceSymbolFiltersState): string {
  const r = referenceFiltersToQueryRecord(state);
  const qs = new URLSearchParams(r).toString();
  return qs ? `?${qs}` : '';
}
